import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthAPI, AdminAPI, currentBranch, extractError, tokens } from "@/lib/api";

const ORIGINAL_ACCESS_KEY = "gms.original_access";
const ORIGINAL_REFRESH_KEY = "gms.original_refresh";

const AuthCtx = createContext({
    user: null,
    tenant: null,
    permissions: [],
    branches: [],
    currentBranchId: null,
    loading: true,
    login: async () => {},
    logout: async () => {},
    refresh: async () => {},
    switchBranch: () => {},
    startImpersonation: async () => ({ ok: false }),
    exitImpersonation: async () => ({ ok: false }),
});

export function AuthProvider({ children }) {
    const [state, setState] = useState({
        user: null,
        tenant: null,
        permissions: [],
        branches: [],
        currentBranchId: currentBranch.get(),
        // Only "loading" if we actually have a token to validate; otherwise the
        // user is logged out and we render immediately (no set-state-in-effect).
        loading: Boolean(tokens.getAccess()),
    });

    const hydrate = useCallback(async () => {
        if (!tokens.getAccess()) {
            return;
        }
        try {
            const me = await AuthAPI.me();
            let cb = currentBranch.get();
            const allowedIds = (me.branches || []).map((b) => b.id);
            // Platform admins outside impersonation have no branch scope.
            if (me.user.is_platform_admin && !me.user.impersonated_by) {
                cb = null;
                currentBranch.set(null);
            } else if (!cb || !allowedIds.includes(cb)) {
                cb = me.user.default_branch_id || (allowedIds[0] ?? null);
                currentBranch.set(cb);
            }
            setState({
                user: me.user,
                tenant: me.tenant,
                permissions: me.permissions,
                branches: me.branches,
                currentBranchId: cb,
                loading: false,
            });
        } catch {
            tokens.clear();
            setState({
                user: null,
                tenant: null,
                permissions: [],
                branches: [],
                currentBranchId: null,
                loading: false,
            });
        }
    }, []);

    // Bootstrap auth on mount: validate any stored token and populate session.
    // Canonical fetch-on-mount pattern (hydrate sets state only after its async call).
    useEffect(() => {
        hydrate();
    }, [hydrate]);

    const login = useCallback(
        async (email, password) => {
            try {
                const res = await AuthAPI.login(email, password);
                tokens.set(res);
                // Clear any leftover impersonation stash from a previous session.
                sessionStorage.removeItem(ORIGINAL_ACCESS_KEY);
                sessionStorage.removeItem(ORIGINAL_REFRESH_KEY);
                await hydrate();
                return { ok: true };
            } catch (err) {
                return { ok: false, message: extractError(err) };
            }
        },
        [hydrate]
    );

    const logout = useCallback(async () => {
        try {
            await AuthAPI.logout();
        } catch (err) {
            // Best-effort: a failed server-side logout must not block the client
            // from clearing its session, but we log it for observability.
            console.warn("Logout request failed; clearing local session anyway:", err);
        }
        tokens.clear();
        sessionStorage.removeItem(ORIGINAL_ACCESS_KEY);
        sessionStorage.removeItem(ORIGINAL_REFRESH_KEY);
        currentBranch.set(null);
        setState({
            user: null,
            tenant: null,
            permissions: [],
            branches: [],
            currentBranchId: null,
            loading: false,
        });
    }, []);

    const switchBranch = useCallback((id) => {
        currentBranch.set(id);
        setState((s) => ({ ...s, currentBranchId: id }));
    }, []);

    const startImpersonation = useCallback(
        async (tenantId) => {
            try {
                const r = await AdminAPI.impersonate(tenantId);
                // Stash the admin's own session so we can restore it on exit.
                sessionStorage.setItem(ORIGINAL_ACCESS_KEY, tokens.getAccess() || "");
                sessionStorage.setItem(ORIGINAL_REFRESH_KEY, tokens.getRefresh() || "");
                // Swap to the impersonation access token. Wipe the refresh token —
                // a 15-minute impersonation must NOT be silently refreshed back
                // into an admin-scope token.
                localStorage.setItem("gms.access", r.access_token);
                localStorage.removeItem("gms.refresh");
                // Force-rehydrate with the new identity.
                await hydrate();
                return { ok: true };
            } catch (err) {
                return { ok: false, message: extractError(err) };
            }
        },
        [hydrate]
    );

    const exitImpersonation = useCallback(async () => {
        try {
            await AdminAPI.endImpersonation();
        } catch (err) {
            // Server-side is best-effort; we still restore the admin token, but
            // surface the failure in the console for debugging.
            console.warn("End-impersonation request failed; restoring admin token anyway:", err);
        }
        const orig = sessionStorage.getItem(ORIGINAL_ACCESS_KEY);
        const origRefresh = sessionStorage.getItem(ORIGINAL_REFRESH_KEY);
        sessionStorage.removeItem(ORIGINAL_ACCESS_KEY);
        sessionStorage.removeItem(ORIGINAL_REFRESH_KEY);
        if (!orig) {
            // Lost the original session (page reload wiped sessionStorage).
            tokens.clear();
            currentBranch.set(null);
            setState({ user: null, tenant: null, permissions: [], branches: [],
                       currentBranchId: null, loading: false });
            return { ok: false, requiresRelogin: true };
        }
        localStorage.setItem("gms.access", orig);
        if (origRefresh) localStorage.setItem("gms.refresh", origRefresh);
        currentBranch.set(null);
        await hydrate();
        return { ok: true };
    }, [hydrate]);

    const value = useMemo(
        () => ({
            ...state,
            login,
            logout,
            refresh: hydrate,
            switchBranch,
            startImpersonation,
            exitImpersonation,
        }),
        [state, login, logout, hydrate, switchBranch, startImpersonation, exitImpersonation],
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export const hasPerm = (permissions, perm) => Array.isArray(permissions) && permissions.includes(perm);
